import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { AppState } from '../../app/store/store'
import ProformaInvoiceEntity from '../../db/entities/proforma_invoice/ProformaInvoiceEntity'
import { selectCurrentWorkspaceId } from '../workspacesSlice'
import { getProformaInvoiceTempLink } from '../../api/proformainvoiceAPI'
import {
    createProformaInvoice as apiCreateProformaInvoice,
    updateProformaInvoice as apiUpdateProformaInvoice,
    fetchProformaInvoice as apiFetchProformaInvoice,
    deleteProformaInvoice as apiDeleteProformaInvoice,
    dismissProformaInvoiceReview as apiDismissProformaInvoiceReview,
    updateProformaInvoiceFromQuote as apiUpdateProformaInvoiceFromQuote,
    patchProformaInvoice as apiPatchProformaInvoice,
} from '../../api/proformaInvoices/api'
import { ProformaInvoiceUpsertPayload } from '../../api/proformaInvoices/types'
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice'
import { container } from '../../app/ioc/container'
import { TYPES } from '../../app/ioc/types'
import { DbManager } from '../../db/dbManager'
import {
    sortByStatus,
    filterByContactName,
    sortByCreatedDate,
    sortByDueDate,
} from './utils'

interface SliceState {
    list: ProformaInvoiceEntity[]
    tempLinks: Record<string, string>
}

const initialState: SliceState = {
    list: [],
    tempLinks: {},
}

// Thunk to fetch a temporary link
export const fetchProformaInvoiceTempLink = createAsyncThunk<
    { uuid: string; link: string },
    string,
    { state: AppState }
>('proformaInvoices/fetchTempLink', async (uuid, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    const link = await getProformaInvoiceTempLink(uuid, workspaceId)
    return { uuid, link }
})

// The list is fed from the local database (rxDbConnector), so API results are
// written there too; the stream would deliver them, but only on the next sync.
const saveToLocalDb = async (invoice: ProformaInvoiceEntity) => {
    const db = await container.get<DbManager>(TYPES.DbManager).getDb()
    await db['proforma-invoices'].bulkUpsert([invoice])
    return invoice
}

const removeFromLocalDb = async (uuid: string) => {
    const db = await container.get<DbManager>(TYPES.DbManager).getDb()
    await db['proforma-invoices'].bulkRemove([uuid])
}

export const createProformaInvoice = createAsyncThunk<
    ProformaInvoiceEntity,
    ProformaInvoiceDraft,
    { state: AppState }
>('proformaInvoices/create', async (draft, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    return saveToLocalDb(await apiCreateProformaInvoice(draft, workspaceId))
})

export const updateProformaInvoice = createAsyncThunk<
    ProformaInvoiceEntity,
    { draft: ProformaInvoiceDraft; initialDraft: ProformaInvoiceDraft },
    { state: AppState }
>('proformaInvoices/update', async ({ draft, initialDraft }, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    return saveToLocalDb(await apiUpdateProformaInvoice(draft, initialDraft, workspaceId))
})

export const patchProformaInvoice = createAsyncThunk<
    ProformaInvoiceEntity,
    ProformaInvoiceUpsertPayload,
    { state: AppState }
>('proformaInvoices/patch', async (payload, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    return saveToLocalDb(await apiPatchProformaInvoice(payload, workspaceId))
})

export const refreshProformaInvoice = createAsyncThunk<
    ProformaInvoiceEntity,
    string,
    { state: AppState }
>('proformaInvoices/refresh', async (uuid, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    return saveToLocalDb(await apiFetchProformaInvoice(uuid, workspaceId))
})

export const dismissProformaInvoiceReview = createAsyncThunk<
    ProformaInvoiceEntity,
    string,
    { state: AppState }
>('proformaInvoices/dismissReview', async (uuid, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    return saveToLocalDb(await apiDismissProformaInvoiceReview(uuid, workspaceId))
})

export const updateProformaInvoiceFromQuote = createAsyncThunk<
    ProformaInvoiceEntity,
    string,
    { state: AppState }
>('proformaInvoices/updateFromQuote', async (uuid, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    return saveToLocalDb(await apiUpdateProformaInvoiceFromQuote(uuid, workspaceId))
})

export const deleteProformaInvoice = createAsyncThunk<
    string,
    string,
    { state: AppState }
>('proformaInvoices/delete', async (uuid, { getState }) => {
    const workspaceId = selectCurrentWorkspaceId(getState())
    await apiDeleteProformaInvoice(uuid, workspaceId)
    await removeFromLocalDb(uuid)
    return uuid
})

const slice = createSlice({
    name: 'proformaInvoices',
    initialState,
    reducers: {
        // Overwrite the entire list (e.g. from your stream provider)
        setProformaInvoices(state, action: PayloadAction<ProformaInvoiceEntity[]>) {
            state.list = action.payload
        },
        upsertProformaInvoice(state, action: PayloadAction<ProformaInvoiceEntity>) {
            const invoice = action.payload
            const index = state.list.findIndex(item => item.uuid === invoice.uuid)
            if (index >= 0) {
                state.list[index] = invoice
            } else {
                state.list.push(invoice)
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchProformaInvoiceTempLink.fulfilled, (state, action) => {
            const { uuid, link } = action.payload
            state.tempLinks[uuid] = link
        })
        builder.addCase(createProformaInvoice.fulfilled, (state, action) => {
            const invoice = action.payload
            const index = state.list.findIndex(item => item.uuid === invoice.uuid)
            if (index >= 0) {
                state.list[index] = invoice
            } else {
                state.list.push(invoice)
            }
        })
        builder.addCase(updateProformaInvoice.fulfilled, (state, action) => {
            const invoice = action.payload
            const index = state.list.findIndex(item => item.uuid === invoice.uuid)
            if (index >= 0) {
                state.list[index] = invoice
            } else {
                state.list.push(invoice)
            }
        })
        builder.addCase(patchProformaInvoice.fulfilled, (state, action) => {
            const invoice = action.payload
            const index = state.list.findIndex(item => item.uuid === invoice.uuid)
            if (index >= 0) {
                state.list[index] = invoice
            } else {
                state.list.push(invoice)
            }
        })
        builder.addCase(deleteProformaInvoice.fulfilled, (state, action) => {
            state.list = state.list.filter(item => item.uuid !== action.payload)
        })
        builder.addCase(refreshProformaInvoice.fulfilled, (state, action) => {
            const invoice = action.payload
            const index = state.list.findIndex(item => item.uuid === invoice.uuid)
            if (index >= 0) {
                state.list[index] = invoice
            } else {
                state.list.push(invoice)
            }
        })
    },
})

export const { setProformaInvoices, upsertProformaInvoice } = slice.actions
export const reducer = slice.reducer
export default reducer

// Basic selectors
export const selectProformaInvoices = (state: AppState): ProformaInvoiceEntity[] =>
    state.proformaInvoices.list

export const selectProformaInvoiceTempLink =
    (uuid: string) =>
        (state: AppState): string | undefined =>
            state.proformaInvoices.tempLinks[uuid]

// Memoized selectors
export const selectInvoicesByStatus = createSelector(
    selectProformaInvoices,
    invoices => sortByStatus(invoices as ProformaInvoiceEntity[])
)

export const makeSelectInvoicesByContactName = (searchTerm: string) =>
    createSelector(selectProformaInvoices, invoices =>
        filterByContactName(invoices, searchTerm)
    )

export const selectInvoicesByCreatedAsc = createSelector(
    selectProformaInvoices,
    invoices => sortByCreatedDate(invoices, 'asc')
)

export const selectInvoicesByCreatedDesc = createSelector(
    selectProformaInvoices,
    invoices => sortByCreatedDate(invoices, 'desc')
)

export const selectInvoicesByDueAsc = createSelector(
    selectProformaInvoices,
    invoices => sortByDueDate(invoices, 'asc')
)

export const selectInvoicesByDueDesc = createSelector(
    selectProformaInvoices,
    invoices => sortByDueDate(invoices, 'desc')
)

export const makeSelectFilteredSortedByDue = (searchTerm: string) =>
    createSelector(selectProformaInvoices, invoices => {
        const filtered = filterByContactName(invoices, searchTerm)
        return sortByDueDate(filtered, 'asc')
    })
